<template>
  <span class="rd-cols">
    <report-checklist-menu
      v-if="items.length >= MIN_ITEMS"
      :label="$t('visible_columns')"
      :items="items"
      :value="visibleValues"
      @input="setVisible"
    ></report-checklist-menu>
  </span>
</template>

<script>
import ReportChecklistMenu from "./ReportChecklistMenu.vue";
import { copyText, rowsToTsv } from "./copyTable";
import { QUERY_KEYS } from "./reportQuery";

const MIN_ITEMS = 3;
const HIDDEN_ATTR = "data-rc-hidden";
const ARROWS = /[↑↓▲▼⇅↕]/g;

const clean = (text) => String(text || "").replace(ARROWS, "").replace(/\s+/g, " ").trim();

/** Naziv kolone iz zaglavlja; oznaka smene ima kratak i pun naziv, pa se uzima pun ("Smena 1", ne "Sm. 1Smena 1"). */
const label = (th) => {
  const full = th.querySelector(".rs-tag__sr");
  return clean(full ? full.textContent : th.textContent);
};

/**
 * "Vidljive kolone" za svaku tabelu izveštaja: dugme u traci alata (ReportTools) čita zaglavlja tabela u svom
 * ekranu i krije kolone koje korisnik isključi. Izbor se pamti po ekranu (a ne po jeziku kolone, pa ga menjaju
 * samo oznake). Kolona sa nazivom reda (is-name) i redni broj ostaju uvek.
 *
 * Kolone su zaglavlja jedne tabele; ekran sa više tabela (npr. po smenama) krije istoimene kolone u svim.
 * Sakrivanje je samo prikaz (atribut na ćelijama, pa ga Vue ne briše pri osvežavanju); štampa i Excel idu iz podataka
 * ekrana, a "Kopiraj tabelu" kopira vidljive kolone kad je neka sakrivena.
 */
export default {
  name: "ReportColumnsMenu",
  components: { ReportChecklistMenu },
  data: () => ({
    MIN_ITEMS,
    items: [],
    hidden: [],
    locked: [],
  }),
  computed: {
    storageKey() {
      const query = this.$route.query || {};
      const parts = [this.$route.path, query[QUERY_KEYS.report], query[QUERY_KEYS.view]].filter(Boolean);
      return `report-columns:${parts.join(":")}`;
    },
    visibleValues() {
      return this.items.map((item) => item.value).filter((value) => !this.hidden.includes(value));
    },
  },
  watch: {
    storageKey: {
      immediate: true,
      handler() {
        this.hidden = this.readHidden();
        this.schedule();
      },
    },
    hidden() {
      this.schedule();
    },
  },
  mounted() {
    this.screen = this.$el.closest(".rd-screen") || this.$el.parentElement;
    if (typeof MutationObserver !== "undefined" && this.screen) {
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.screen, { childList: true, subtree: true });
    }
    this.schedule();
  },
  beforeDestroy() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  },
  methods: {
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.refresh);
    },
    tables() {
      if (!this.screen) {
        return [];
      }
      return [...this.screen.querySelectorAll("table.rd-tbl")].filter(
        (table) => table.tHead && table.tHead.rows.length === 1 && !table.parentElement.closest("table")
      );
    },
    /** Izbor kolona: nazivi iz zaglavlja, bez rednog broja i praznih (strelica) kolona. */
    refresh() {
      if (!this.screen || !this.screen.isConnected) {
        return;
      }
      const items = [];
      const locked = [];
      for (const table of this.tables()) {
        const head = [...table.tHead.rows[0].cells];
        const firstRow = table.tBodies[0] && table.tBodies[0].rows[0];
        head.forEach((th, index) => {
          const text = label(th);
          if (!text || th.classList.contains("rd-col-num") || text === "#") {
            return;
          }
          const isName = firstRow && firstRow.cells[index] && firstRow.cells[index].classList.contains("is-name");
          if (isName && !locked.includes(text)) {
            locked.push(text);
          }
          if (!items.some((item) => item.value === text)) {
            items.push({ value: text, text });
          }
        });
      }
      const selectable = items.filter((item) => !locked.includes(item.value));
      if (JSON.stringify(selectable) !== JSON.stringify(this.items)) {
        this.items = selectable;
      }
      this.locked = locked;
      this.apply();
    },
    apply() {
      const hidden = this.hidden.filter((value) => !this.locked.includes(value));
      for (const table of this.tables()) {
        const head = [...table.tHead.rows[0].cells];
        const hide = head.map((th) => hidden.includes(label(th)));
        const rows = [...table.tHead.rows, ...[...table.tBodies].flatMap((body) => [...body.rows]), ...(table.tFoot ? [...table.tFoot.rows] : [])];
        for (const row of rows) {
          const cells = [...row.cells];
          const plain = cells.length === head.length && cells.every((cell) => cell.colSpan <= 1);
          cells.forEach((cell, index) => {
            const want = plain && hide[index];
            if (want !== cell.hasAttribute(HIDDEN_ATTR)) {
              if (want) {
                cell.setAttribute(HIDDEN_ATTR, "");
              } else {
                cell.removeAttribute(HIDDEN_ATTR);
              }
            }
          });
        }
      }
    },
    setVisible(values) {
      this.hidden = this.items.map((item) => item.value).filter((value) => !values.includes(value));
      this.writeHidden();
    },
    readHidden() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.storageKey) || "[]");
        return Array.isArray(stored) ? stored.filter((value) => typeof value === "string") : [];
      } catch (error) {
        return [];
      }
    },
    writeHidden() {
      try {
        if (this.hidden.length) {
          localStorage.setItem(this.storageKey, JSON.stringify(this.hidden));
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch (error) {
        // Bez localStorage-a izbor važi dok je ekran otvoren.
      }
    },
    /**
     * Kopira vidljive kolone svih tabela ekrana (tabulatorima). Vraća null kad nema sakrivenih kolona, da ekran
     * kopira po svom; inače true/false da li je kopiranje uspelo.
     */
    async copyVisible() {
      const hidden = this.hidden.filter((value) => this.items.some((item) => item.value === value));
      if (!hidden.length) {
        return null;
      }
      const blocks = [];
      for (const table of this.tables()) {
        const head = [...table.tHead.rows[0].cells];
        const bodyRows = [...[...table.tBodies].flatMap((body) => [...body.rows]), ...(table.tFoot ? [...table.tFoot.rows] : [])].filter(
          (row) => row.cells.length === head.length
        );
        const keep = head
          .map((th, index) => index)
          .filter((index) => !head[index].hasAttribute(HIDDEN_ATTR))
          .filter((index) => label(head[index]) || bodyRows.some((row) => clean(row.cells[index].textContent)));
        blocks.push(
          rowsToTsv(
            keep.map((index) => label(head[index])),
            bodyRows.map((row) => keep.map((index) => clean(row.cells[index].textContent)))
          )
        );
      }
      return copyText(blocks.join("\n\n"));
    },
  },
};
</script>
